import { SetupFutureUsageElementsProp } from '@frontend/api-weave-pay';
import { PaymentsFeatureFlags, useMerchants } from '@frontend/payments-hooks';
import { useAppFlagStore } from '@frontend/shared';
import { PaymentConfigName } from './use-payment-method-config';
import { useWeaveAppStripe } from './use-weave-app-stripe';
import { useWeaveAppStripeConfig } from './use-weave-app-stripe-config';

export enum PaymentsFlowType {
  CardOnFilePayment = 'Card on File Payment',
  ManualCardEntry = 'Manual Card Entry',
  AddCardOnFile = 'Add Card On File',
  Terminal = 'Terminal',
}

export type UseStripeElementsOptionsProps = {
  locationId: string | undefined;
  type: PaymentsFlowType;
  amount?: number;
  paymentConfigName?: PaymentConfigName;
  disableACH?: boolean;
};

const setupModeTypes = [PaymentsFlowType.AddCardOnFile, PaymentsFlowType.Terminal];

const cardOnFilePaymentMethodOptions = { us_bank_account: { verification_method: 'instant' as const } };

export const useStripeElementsOptions = ({
  amount = 50,
  type,
  paymentConfigName,
  disableACH,
  locationId,
}: UseStripeElementsOptionsProps) => {
  const {
    firstLocation: { activeProcessor, merchantFetched, opsType },
  } = useMerchants(locationId);

  const { stripePromise, stripeKey, isLoading: isLoadingLibrary, error, alertStripeError } = useWeaveAppStripe(opsType);

  const { getFeatureFlagValue } = useAppFlagStore();
  const enableACHOnFile = getFeatureFlagValue({
    locationIds: locationId ? [locationId] : [],
    flagName: PaymentsFeatureFlags.ACHOnFile,
  });
  const isAddCardOnFileFlow = type === PaymentsFlowType.AddCardOnFile;
  const isAchEnabled = !disableACH && isAddCardOnFileFlow && enableACHOnFile;

  paymentConfigName = isAchEnabled ? 'cardAndACH' : paymentConfigName;
  const paymentMethodConfiguration = useWeaveAppStripeConfig({
    paymentConfigName: paymentConfigName ?? 'cardOnly',
    locationId,
  });

  const mode: 'setup' | 'payment' = setupModeTypes.includes(type) ? 'setup' : 'payment';
  const setupFutureUsage = isAddCardOnFileFlow ? SetupFutureUsageElementsProp.OffSession : undefined;

  const isLoading = !merchantFetched || !stripeKey || isLoadingLibrary || paymentMethodConfiguration === null;

  return {
    stripeKey,
    stripePromise,
    isLoading,
    error,
    alertStripeError,
    options: {
      mode,
      setupFutureUsage,
      currency: 'usd',
      paymentMethodConfiguration,
      onBehalfOf: activeProcessor?.stripeId,
      ...(mode !== 'setup' ? { amount } : {}),
      ...(isAddCardOnFileFlow ? { paymentMethodOptions: cardOnFilePaymentMethodOptions } : {}),
    },
  };
};
